import {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
    ReactNode,
} from 'react';
import { DineoutTracking } from '@dineouticeland/do-tracking';
const DEBUG = import.meta.env.VITE_TRACKING_DEBUG;

type CompanyId = string | number | null | undefined;

type TrackingContextType = {
    setCompanyIdentifier: (id: CompanyId) => void;
};

const TrackingContext = createContext<TrackingContextType | null>(null);


export const TrackingProvider = ({ children }: { children: ReactNode }) => {
    const [companyIdentifier, setCompanyIdentifierState] = useState<string | null>(
        null,
    );

    // Stable setter: pages set the current restaurant/company id; Layout clears
    // it on navigation. Stringifies (do-tracking expects a string identifier).
    const setCompanyIdentifier = useCallback(
        (id: CompanyId) =>
            setCompanyIdentifierState(id != null ? id.toString() : null),
        [],
    );

    return (
        <TrackingContext.Provider value={{ setCompanyIdentifier }}>
            <DineoutTracking
                platform="dineout"
                companyIdentifier={companyIdentifier ?? undefined}
                debug={DEBUG}
            />
            {children}
        </TrackingContext.Provider>
    );
};

export const useTrackingContext = () => {
    const ctx = useContext(TrackingContext);
    if (!ctx) {
        throw new Error('useTrackingContext must be used within TrackingProvider');
    }
    return ctx;
};

/**
 * Owns the restaurant/company tracking scope for the lifetime of the caller:
 * sets the identifier while `companyId` is known and clears it on unmount, so
 * the scope never leaks onto the next page. Pass the id of whatever restaurant
 * the current page/store represents.
 */
export const useCompanyIdentifier = (companyId: CompanyId) => {
    const { setCompanyIdentifier } = useTrackingContext();

    useEffect(() => {
        if (companyId != null) {
            setCompanyIdentifier(companyId);
        }
        return () => setCompanyIdentifier(null);
    }, [companyId, setCompanyIdentifier]);
};
